import React, { useState, useEffect } from 'react';
import {
  FileText,
  Instagram,
  Check,
  X,
  Eye,
  Send,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  XCircle,
  Edit2
} from 'lucide-react';
import {
  PendingContent,
  getPendingContent,
  getAllContent,
  approveContent,
  rejectContent,
  publishNews,
  updateDraft
} from '../../services/contentApprovalService';

interface ContentApprovalTabProps {
  adminEmail: string;
  onUpdate?: () => void;
}

const ContentApprovalTab: React.FC<ContentApprovalTabProps> = ({ adminEmail, onUpdate }) => {
  const [pendingItems, setPendingItems] = useState<PendingContent[]>([]);
  const [historyItems, setHistoryItems] = useState<PendingContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<'pending' | 'history'>('pending');
  const [selectedItem, setSelectedItem] = useState<PendingContent | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState<Partial<PendingContent>>({});

  useEffect(() => {
    loadContent();
  }, []);

  // Escape close
  useEffect(() => {
    if (!selectedItem && !showRejectModal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (showRejectModal) {
        setShowRejectModal(false);
      } else if (selectedItem) {
        setSelectedItem(null);
        setEditMode(false);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedItem, showRejectModal]);

  const loadContent = async () => {
    try {
      setLoading(true);
      const [pending, all] = await Promise.all([
        getPendingContent(),
        getAllContent()
      ]);
      setPendingItems(pending);
      setHistoryItems(all.filter(item => item.status !== 'pending'));
    } catch (error) {
      console.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (item: PendingContent) => {
    setActionLoading(item.id);
    try {
      await approveContent(item.id, adminEmail);
      await loadContent();
      onUpdate?.();
      setSelectedItem(null);
    } catch (error) {
      console.error('Error approving:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async () => {
    if (!selectedItem) return;
    setActionLoading(selectedItem.id);
    try {
      await rejectContent(selectedItem.id, adminEmail, rejectReason);
      await loadContent();
      onUpdate?.();
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedItem(null);
    } catch (error) {
      console.error('Error rejecting:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePublish = async (item: PendingContent) => {
    if (item.contentType !== 'website_news') {
      alert('Instagram publishing requires API integration. Please post manually.');
      return;
    }
    setActionLoading(item.id);
    try {
      await publishNews(item.id);
      await loadContent();
      onUpdate?.();
      alert('News published to website!');
    } catch (error) {
      console.error('Error publishing:', error);
      alert('Failed to publish. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedItem) return;
    setActionLoading(selectedItem.id);
    try {
      await updateDraft(selectedItem.id, editData);
      await loadContent();
      setEditMode(false);
      setEditData({});
      setSelectedItem(null);
    } catch (error) {
      console.error('Error saving edit:', error);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp?.toDate) return 'Unknown';
    return timestamp.toDate().toLocaleString();
  };

  const getStatusBadge = (status: string) => {
    const baseClass = 'inline-flex items-center gap-1.5 px-2 py-0.5 font-mono text-[10px] tracking-micro uppercase border';
    switch (status) {
      case 'pending':
        return <span className={`${baseClass} bg-brass/10 text-brass border-brass/30`}><Clock className="w-3 h-3" strokeWidth={1.5} /> Pending</span>;
      case 'approved':
        return <span className={`${baseClass} bg-olive/15 text-olive border-olive/40`}><CheckCircle className="w-3 h-3" strokeWidth={1.5} /> Approved</span>;
      case 'rejected':
        return <span className={`${baseClass} bg-ember-soft/25 text-ember border-ember/30`}><XCircle className="w-3 h-3" strokeWidth={1.5} /> Rejected</span>;
      case 'published':
        return <span className={`${baseClass} bg-steel/10 text-ink-700 border-steel/30`}><Send className="w-3 h-3" strokeWidth={1.5} /> Published</span>;
      default:
        return null;
    }
  };

  const renderContentPreview = (item: PendingContent) => {
    if (item.contentType === 'website_news') {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="w-4 h-4 text-steel" strokeWidth={1.5} />
            <span className="font-mono text-[10px] tracking-micro uppercase text-steel">Website news</span>
            <span className="px-2 py-0.5 bg-paper text-ink-700 text-xs">{item.category}</span>
          </div>
          <h3 className="font-editorial italic text-lg text-ink-950 tracking-tight">{item.title}</h3>
          <p className="text-ink-700 text-sm">{item.summary}</p>
        </div>
      );
    } else {
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Instagram className="w-4 h-4 text-ember" strokeWidth={1.5} />
            <span className="font-mono text-[10px] tracking-micro uppercase text-ember">Instagram post</span>
          </div>
          {item.imageUrl && (
            <img
              src={item.imageUrl}
              alt="Instagram preview"
              className="w-full max-w-sm border border-ink-200"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x400?text=Image+Preview';
              }}
            />
          )}
          <p className="text-ink-700 text-sm whitespace-pre-wrap">{item.caption?.substring(0, 200)}...</p>
        </div>
      );
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-ember" strokeWidth={1.5} />
      </div>
    );
  }

  const inputClass =
    'w-full px-4 py-2.5 bg-paper-bright border border-ink-200 text-sm text-ink-900 placeholder:text-ink-500 focus:border-ember focus:outline-none focus:ring-1 focus:ring-ember/20 transition-colors duration-150';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-mono text-[11px] tracking-micro uppercase text-ember mb-3">— Content</p>
          <h2 className="font-editorial italic font-light text-3xl text-ink-950 tracking-tight">
            Content approval.
          </h2>
          <p className="text-ink-700 text-sm mt-1">Review and approve AI-generated content before publishing.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setActiveView('pending')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors duration-150 border ${
              activeView === 'pending'
                ? 'bg-ember text-paper-warm border-ember'
                : 'bg-paper-bright text-ink-700 border-ink-200 hover:border-ink-300 hover:bg-paper'
            }`}
          >
            Pending <span className="font-mono opacity-75">({pendingItems.length})</span>
          </button>
          <button
            onClick={() => setActiveView('history')}
            className={`px-4 py-2.5 text-sm font-medium transition-colors duration-150 border ${
              activeView === 'history'
                ? 'bg-ember text-paper-warm border-ember'
                : 'bg-paper-bright text-ink-700 border-ink-200 hover:border-ink-300 hover:bg-paper'
            }`}
          >
            History
          </button>
        </div>
      </div>

      {/* Content List */}
      {activeView === 'pending' && pendingItems.length === 0 ? (
        <div className="bg-paper-bright border border-ink-200 p-12 text-center">
          <CheckCircle className="w-12 h-12 text-olive mx-auto mb-4" strokeWidth={1.5} />
          <h3 className="font-editorial italic text-2xl text-ink-950 mb-2 tracking-tight">All caught up.</h3>
          <p className="text-ink-700 text-sm">No content pending approval. The AI agent will create drafts that appear here.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {(activeView === 'pending' ? pendingItems : historyItems).map((item) => (
            <div key={item.id} className="bg-paper-bright border border-ink-200 p-6 hover:border-ink-300 transition-colors duration-150">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  {renderContentPreview(item)}
                  <div className="flex items-center gap-4 mt-4 text-xs text-ink-500 flex-wrap">
                    <span>Created: {formatDate(item.createdAt)}</span>
                    <span>Source: {item.source}</span>
                    {getStatusBadge(item.status)}
                  </div>
                </div>

                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => {
                      setSelectedItem(item);
                      setEditData({
                        title: item.title,
                        summary: item.summary,
                        fullContent: item.fullContent,
                        category: item.category,
                        caption: item.caption,
                        imageUrl: item.imageUrl,
                      });
                    }}
                    className="p-2 text-ink-700 hover:text-ink-950 hover:bg-paper transition-colors duration-150"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" strokeWidth={1.5} />
                  </button>

                  {item.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleApprove(item)}
                        disabled={actionLoading === item.id}
                        className="p-2 text-olive hover:bg-olive/15 transition-colors duration-150 disabled:opacity-50"
                        title="Approve"
                      >
                        {actionLoading === item.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                        ) : (
                          <Check className="w-4 h-4" strokeWidth={1.5} />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setSelectedItem(item);
                          setShowRejectModal(true);
                        }}
                        className="p-2 text-ember hover:bg-ember-soft/25 transition-colors duration-150"
                        title="Reject"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </>
                  )}

                  {item.status === 'approved' && item.contentType === 'website_news' && (
                    <button
                      onClick={() => handlePublish(item)}
                      disabled={actionLoading === item.id}
                      className="px-3 py-1.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial disabled:opacity-50 flex items-center gap-1.5"
                    >
                      {actionLoading === item.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <>
                          <Send className="w-4 h-4" strokeWidth={1.5} />
                          Publish
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Preview/Edit Modal */}
      {selectedItem && !showRejectModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={() => { setSelectedItem(null); setEditMode(false); }} />
          <div className="relative bg-paper-bright shadow-float w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-forge text-paper px-6 py-4 flex items-center justify-between z-10">
              <h2 className="font-editorial italic text-lg text-paper-warm flex items-center gap-2 tracking-tight">
                {selectedItem.contentType === 'website_news' ? (
                  <><FileText className="w-4 h-4" strokeWidth={1.5} /> Website news preview</>
                ) : (
                  <><Instagram className="w-4 h-4" strokeWidth={1.5} /> Instagram preview</>
                )}
              </h2>
              <div className="flex items-center gap-2">
                {selectedItem.status === 'pending' && (
                  <button
                    onClick={() => setEditMode(!editMode)}
                    className="w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
                  >
                    <Edit2 className="w-4 h-4" strokeWidth={1.5} />
                  </button>
                )}
                <button
                  onClick={() => { setSelectedItem(null); setEditMode(false); }}
                  className="w-9 h-9 flex items-center justify-center text-paper/70 hover:text-paper hover:bg-paper/10 transition-colors duration-150"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>

            <div className="p-6 lg:p-8">
              {selectedItem.contentType === 'website_news' ? (
                <div className="space-y-5">
                  {editMode ? (
                    <>
                      <div>
                        <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Title</label>
                        <input
                          type="text"
                          value={editData.title || ''}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Category</label>
                        <select
                          value={editData.category || ''}
                          onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                          className={`${inputClass} bg-paper-bright`}
                        >
                          <option value="Logistics">Logistics</option>
                          <option value="Market Opportunity">Market Opportunity</option>
                          <option value="Industry Trends">Industry Trends</option>
                          <option value="Product Launch">Product Launch</option>
                          <option value="Alert">Alert</option>
                        </select>
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Summary</label>
                        <textarea
                          value={editData.summary || ''}
                          onChange={(e) => setEditData({ ...editData, summary: e.target.value })}
                          rows={2}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Full content</label>
                        <textarea
                          value={editData.fullContent || ''}
                          onChange={(e) => setEditData({ ...editData, fullContent: e.target.value })}
                          rows={10}
                          className={inputClass}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="inline-block px-3 py-1 bg-steel/10 text-ink-700 font-mono text-[10px] tracking-micro uppercase border border-steel/30">{selectedItem.category}</span>
                      <h3 className="font-editorial italic text-3xl text-ink-950 tracking-tight">{selectedItem.title}</h3>
                      <p className="text-ink-700 font-medium">{selectedItem.summary}</p>
                      <div className="border-t border-ink-200 pt-4 whitespace-pre-wrap text-ink-900 text-sm">{selectedItem.fullContent}</div>
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-5">
                  {editMode ? (
                    <>
                      <div>
                        <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Image URL</label>
                        <input
                          type="text"
                          value={editData.imageUrl || ''}
                          onChange={(e) => setEditData({ ...editData, imageUrl: e.target.value })}
                          className={inputClass}
                        />
                      </div>
                      <div>
                        <label className="block font-mono text-[10px] tracking-micro uppercase text-ink-500 mb-2">Caption</label>
                        <textarea
                          value={editData.caption || ''}
                          onChange={(e) => setEditData({ ...editData, caption: e.target.value })}
                          rows={8}
                          className={inputClass}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      {selectedItem.imageUrl && (
                        <img
                          src={selectedItem.imageUrl}
                          alt="Preview"
                          className="w-full max-w-md mx-auto border border-ink-200"
                        />
                      )}
                      <div className="bg-paper border border-ink-200 p-4 whitespace-pre-wrap text-ink-900 text-sm">{selectedItem.caption}</div>
                    </>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-ink-200">
                {editMode ? (
                  <>
                    <button
                      onClick={() => setEditMode(false)}
                      className="px-4 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={actionLoading === selectedItem.id}
                      className="px-5 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial disabled:opacity-50"
                    >
                      {actionLoading === selectedItem.id ? 'Saving...' : 'Save changes'}
                    </button>
                  </>
                ) : selectedItem.status === 'pending' ? (
                  <>
                    <button
                      onClick={() => {
                        setShowRejectModal(true);
                      }}
                      className="px-4 py-2.5 border border-ember/30 text-ember hover:bg-ember-soft/25 text-sm font-medium transition-colors duration-150"
                    >
                      Reject
                    </button>
                    <button
                      onClick={() => handleApprove(selectedItem)}
                      disabled={actionLoading === selectedItem.id}
                      className="px-5 py-2.5 bg-olive hover:bg-olive/90 text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial disabled:opacity-50 flex items-center gap-2"
                    >
                      {actionLoading === selectedItem.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                      ) : (
                        <Check className="w-4 h-4" strokeWidth={1.5} />
                      )}
                      Approve
                    </button>
                  </>
                ) : selectedItem.status === 'approved' && selectedItem.contentType === 'website_news' ? (
                  <button
                    onClick={() => handlePublish(selectedItem)}
                    disabled={actionLoading === selectedItem.id}
                    className="px-5 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial disabled:opacity-50 flex items-center gap-2"
                  >
                    {actionLoading === selectedItem.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
                    ) : (
                      <Send className="w-4 h-4" strokeWidth={1.5} />
                    )}
                    Publish to website
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-forge-deep/70 backdrop-blur-sm" onClick={() => setShowRejectModal(false)} />
          <div className="relative bg-paper-bright shadow-float w-full max-w-md">
            <div className="p-6 lg:p-8">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-ember-soft/25 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-ember" strokeWidth={1.5} />
                </div>
                <h3 className="font-editorial italic text-2xl text-ink-950 tracking-tight">Reject content.</h3>
              </div>
              <p className="text-ink-700 text-sm mb-4">
                Are you sure you want to reject this content? Optionally provide a reason.
              </p>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for rejection (optional)"
                rows={3}
                className={`${inputClass} mb-4`}
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1 px-4 py-2.5 border border-ink-200 hover:border-ink-300 hover:bg-paper text-ink-700 text-sm font-medium transition-colors duration-150"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={actionLoading === selectedItem.id}
                  className="flex-1 px-4 py-2.5 bg-ember hover:bg-ember-deep text-paper-warm text-sm font-medium transition-colors duration-150 ease-editorial disabled:opacity-50"
                >
                  {actionLoading === selectedItem.id ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentApprovalTab;
